import type { Card } from '@study-decks/shared'

import type { NewScoreInput } from './scores.js'

export interface SpeedMatchItem {
  cardId: string
  text: string
}

export interface SpeedMatchState {
  deckId: string
  classId: string | null
  startedAt: Date
  terms: SpeedMatchItem[]
  definitions: SpeedMatchItem[]
  selectedTermId: string | null
  matches: Map<string, string>
  incorrectAttempts: Map<string, number>
  elapsedSeconds: number
  paused: boolean
}

function seededRandom(seed: number) {
  let value = seed || 1
  return () => {
    value = (value * 48271) % 0x7fffffff
    return value / 0x7fffffff
  }
}

function seedFromCards(cards: Card[]) {
  return cards.reduce((total, card) => total + card.id.charCodeAt(0), cards.length + 17)
}

function shuffle<T>(items: T[], seed: number): T[] {
  const random = seededRandom(seed)
  const next = [...items]
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1))
    const item = next[index]!
    next[index] = next[swapIndex]!
    next[swapIndex] = item
  }
  return next
}

function isSpeedMatchItem(item: SpeedMatchItem | undefined): item is SpeedMatchItem {
  return item !== undefined
}

export function startSpeedMatch(
  deckId: string,
  classId: string | null,
  cards: Card[],
  startedAt = new Date(),
): SpeedMatchState {
  const seed = seedFromCards(cards)
  return {
    deckId,
    classId,
    startedAt,
    terms: shuffle(
      cards.map((card) => ({ cardId: card.id, text: card.term })),
      seed,
    ),
    definitions: shuffle(
      cards.map((card) => ({ cardId: card.id, text: card.definition })),
      seed * 3,
    ),
    selectedTermId: null,
    matches: new Map(),
    incorrectAttempts: new Map(),
    elapsedSeconds: 0,
    paused: false,
  }
}

export function restoreSpeedMatch(
  deckId: string,
  classId: string | null,
  cards: Card[],
  snapshot: {
    startedAt: string
    selectedTermId: string | null
    matches: Record<string, string>
    incorrectAttempts: Record<string, number>
    elapsedSeconds: number
    paused: boolean
    termOrder: string[]
    definitionOrder: string[]
  },
): SpeedMatchState {
  const base = startSpeedMatch(deckId, classId, cards, new Date(snapshot.startedAt))
  const termById = new Map(base.terms.map((term) => [term.cardId, term]))
  const definitionById = new Map(
    base.definitions.map((definition) => [definition.cardId, definition]),
  )
  const orderedTerms = snapshot.termOrder.map((id) => termById.get(id)).filter(isSpeedMatchItem)
  const orderedDefinitions = snapshot.definitionOrder
    .map((id) => definitionById.get(id))
    .filter(isSpeedMatchItem)

  return {
    ...base,
    terms: orderedTerms.length === base.terms.length ? orderedTerms : base.terms,
    definitions:
      orderedDefinitions.length === base.definitions.length ? orderedDefinitions : base.definitions,
    selectedTermId: snapshot.selectedTermId,
    matches: new Map(Object.entries(snapshot.matches)),
    incorrectAttempts: new Map(Object.entries(snapshot.incorrectAttempts)),
    elapsedSeconds: Math.max(0, snapshot.elapsedSeconds),
    paused: snapshot.paused,
  }
}

export function selectTerm(state: SpeedMatchState, termId: string | null): SpeedMatchState {
  if (termId && state.matches.has(termId)) return state
  return { ...state, selectedTermId: termId }
}

export function togglePaused(state: SpeedMatchState): SpeedMatchState {
  return { ...state, paused: !state.paused }
}

export function tickSpeedMatch(state: SpeedMatchState): SpeedMatchState {
  if (state.paused || isSpeedMatchComplete(state)) return state
  return { ...state, elapsedSeconds: state.elapsedSeconds + 1 }
}

export function attemptMatch(
  state: SpeedMatchState,
  termId: string,
  definitionId: string,
): { state: SpeedMatchState; correct: boolean } {
  if (state.matches.has(termId) || Array.from(state.matches.values()).includes(definitionId)) {
    return { state, correct: false }
  }
  if (termId === definitionId) {
    const matches = new Map(state.matches)
    matches.set(termId, definitionId)
    return {
      state: { ...state, matches, selectedTermId: null },
      correct: true,
    }
  }
  const incorrectAttempts = new Map(state.incorrectAttempts)
  incorrectAttempts.set(termId, (incorrectAttempts.get(termId) ?? 0) + 1)
  return {
    state: { ...state, incorrectAttempts, selectedTermId: null },
    correct: false,
  }
}

export function isSpeedMatchComplete(state: SpeedMatchState): boolean {
  return state.matches.size === state.terms.length && state.terms.length > 0
}

export function finishSpeedMatch(state: SpeedMatchState, endedAt = new Date()): NewScoreInput {
  const missedCardIds = state.terms
    .filter((term) => (state.incorrectAttempts.get(term.cardId) ?? 0) > 0)
    .map((term) => term.cardId)
  const attempts =
    state.matches.size +
    Array.from(state.incorrectAttempts.values()).reduce((total, count) => total + count, 0)
  const accuracy = attempts === 0 ? 0 : state.matches.size / attempts
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

  return {
    deckId: state.deckId,
    classId: state.classId,
    goalId: null,
    mode: 'speedMatch',
    startedAt: state.startedAt.toISOString(),
    endedAt: endedAt.toISOString(),
    durationSeconds: Math.max(
      state.elapsedSeconds,
      Math.floor((endedAt.getTime() - state.startedAt.getTime()) / 1000),
    ),
    cardsSeen: state.terms.length,
    cardsCorrect: state.matches.size,
    accuracy: Math.max(0, Math.min(1, accuracy)),
    missedCardIds,
    clientTimezone: timezone,
  }
}
